import React, { useState } from "react";
import { Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

const initial = {
  name: "", code: "", logo_url: "", logo_text: "",
  primary_color: "#1E40AF", secondary_color: "#3B82F6",
};

export function AddHospitalDialog({ onCreated, triggerVariant = "solid" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code) { toast.error("Name and code are required"); return; }
    setSaving(true);
    try {
      const h = await api.createHospital(form);
      toast.success(`${h.name} onboarded`);
      setOpen(false); setForm(initial);
      onCreated && onCreated(h);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add hospital");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-hospital-trigger" className="rounded-full h-9 px-4 text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Add hospital
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Onboard new hospital</DialogTitle>
          <DialogDescription>All portals will be dynamically branded from these colors.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Name</Label>
            <Input data-testid="hospital-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label>Hospital code (slug)</Label>
            <Input data-testid="hospital-code-input" value={form.code}
              placeholder="e.g. apollo"
              onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })} required />
          </div>
          <div>
            <Label>Logo text</Label>
            <Input data-testid="hospital-logo-text-input" value={form.logo_text}
              onChange={(e) => setForm({ ...form, logo_text: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Logo URL (optional)</Label>
            <Input data-testid="hospital-logo-url-input" value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
          </div>
          <div>
            <Label>Primary color</Label>
            <div className="flex items-center gap-2">
              <Input type="color" data-testid="hospital-primary-color-input"
                className="w-14 h-10 p-1 cursor-pointer"
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
              <Input value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Secondary color</Label>
            <div className="flex items-center gap-2">
              <Input type="color" data-testid="hospital-secondary-color-input"
                className="w-14 h-10 p-1 cursor-pointer"
                value={form.secondary_color}
                onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} />
              <Input value={form.secondary_color}
                onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} />
            </div>
          </div>
          <div className="col-span-2 rounded-lg border border-border p-4 flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg" style={{ background: form.primary_color }} />
            <span className="w-8 h-8 rounded-lg" style={{ background: form.secondary_color }} />
            <div className="text-xs text-muted-foreground">
              Preview colors — used sparingly as accents throughout the app.
            </div>
          </div>
          <DialogFooter className="col-span-2 gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="hospital-submit-btn" className="rounded-full">
              {saving ? "Saving…" : "Create hospital"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
